import React, { useCallback, useMemo, useState } from 'react';

import { DateContext } from './context';
import { IDatePanelProps, PanelView } from './DatePicker.types';
import { convertToDate, isValidDate } from './utils';
import DateTable from './DateTable';
import MonthTable from './MonthTable';
import YearTable from './YearTable';
import styles from './datepicker.module.css';

const DatePanel: React.FC<IDatePanelProps> = ({
  value,
  onChange,
  onClose,
  disabledDate,
  dateOptions,
  preDateOptions
}) => {
  const [view, setView] = useState<PanelView>('date');
  const initDate = useMemo(() => convertToDate(value), [value]);
  const [currentDate, setCurrentDate] = useState(initDate);
  const currentYear = useMemo(() => currentDate.getFullYear(), [currentDate]);
  const currentMonth = useMemo(() => currentDate.getMonth(), [currentDate]);

  const backToDateView = useCallback(() => {
    setView('date');
  }, []);

  const context = useMemo(
    () => ({
      currentDate,
      currentYear,
      currentMonth,
      setCurrentDate,
      setView,
      backToDateView,
      disabledDate,
      dateOptions,
      preDateOptions
    }),
    [
      currentDate,
      currentYear,
      currentMonth,
      backToDateView,
      disabledDate,
      dateOptions,
      preDateOptions
    ]
  );

  const selectDate = useCallback(
    (date: Date) => {
      onChange?.(date);
      onClose?.();
    },
    [onChange, onClose]
  );

  const currentView = useMemo(() => {
    switch (view) {
      case 'date': {
        return (
          <DateTable
            date={initDate}
            onSelectDate={selectDate}
            isSelected={isValidDate(value)}
          />
        );
      }
      case 'year': {
        return <YearTable />;
      }
      case 'month': {
        return <MonthTable />;
      }
      default: {
        return null;
      }
    }
  }, [view, value, initDate, selectDate]);

  return (
    <div className={styles.panel}>
      <DateContext.Provider value={context}>{currentView}</DateContext.Provider>
    </div>
  );
};

export default DatePanel;
